'use client';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { ParsePillHeading } from '@/components/ParsePillHeading';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { CustomShadows } from '@/theme';
import { ComponentBlocksStepItem, ComponentBlocksThreeSteps } from '@/types';

import GlobalBlockRenderer, {
  prefixClasses,
  processCustomClassesFromText,
} from '../GlobalBlockRenderer/GlobalBlockRenderer';

export default function ThreeSteps({ block }: { block: ComponentBlocksThreeSteps }) {
  const {
    ThreeStepTitle,
    ThreeStepDescription,
    ThreeStepBlurb,
    Steps = [],
    BlockCta,
    CTAIntro,
    CTAFooter,
    StepImageBackground = true,
    ShowNumbers = true,
    HideArrows = false,
  } = block;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const validSteps = (Steps ?? []).filter(Boolean) as ComponentBlocksStepItem[];
  if (!validSteps.length) return null;

  return (
    <CustomBackground
      displayOptions={
        {
          ...block.ThreeStepsDisplayOptions,
          PaddingTop: block.ThreeStepsDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: block.ThreeStepsDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: block.ThreeStepsDisplayOptions?.PaddingInline ?? 'md',
        } as StrapiDisplayOptions
      }
      backgroundOptions={
        {
          ...block.ThreeStepsBackground,
          BackgroundColor: block.ThreeStepsBackground?.BackgroundColor ?? 'purple-200',
        } as StrapiBackgroundOptions
      }
    >
      {!ThreeStepBlurb && ThreeStepTitle && <ParsePillHeading title={ThreeStepTitle} />}

      {!ThreeStepBlurb && ThreeStepDescription && (
        <Typography
          variant="body1"
          align="center"
          sx={{
            mx: 'auto',
            maxWidth: '100%',
            overflowX: 'hidden',
            whiteSpace: 'wrap',
          }}
        >
          {ThreeStepDescription}
        </Typography>
      )}

      {ThreeStepBlurb && <GlobalBlockRenderer content={ThreeStepBlurb || []} />}

      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="stretch"
        spacing={isMobile ? 4 : 3}
        sx={{ width: `calc(100% - ${theme.spacing(5)} * 2)`, padding: 5 }}
      >
        {validSteps.map((step, index) => {
          const cta = step.StepItemCta;
          const hasCta = cta && cta.ShowButton && cta.ButtonText;
          const image = getImageUrl({ image: step.Image, size: 'thumbnail' });
          const { classes: titleClasses, cleanedText: cleanedTitle } = processCustomClassesFromText(
            step.Title
          );

          return (
            <React.Fragment key={index}>
              <Box
                sx={{
                  flex: '1 1 0%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  minWidth: 0,
                }}
                data-component={'ThreeSteps_step'}
              >
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative',
                    textAlign: 'left',
                    px: 3,
                    py: 2.5,
                    height: '100%',
                    transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                    borderRadius: 6,
                    boxShadow: (theme.shadows as CustomShadows).d,
                    background: theme.palette.gradients.purpleWhite,
                    '&:hover': {
                      background: theme.palette.common.white,
                      boxShadow: (theme.shadows as CustomShadows).c,
                      transform: { xs: 'none', md: 'translateY(-3px)' },
                    },
                  }}
                >
                  {/* Step number */}
                  {ShowNumbers && (
                    <Typography
                      variant="h3"
                      fontWeight={700}
                      sx={{
                        position: 'absolute',
                        top: theme.spacing(2.15),
                        left: theme.spacing(2.65),
                        color: theme.palette.purple[400],
                        lineHeight: 1,
                      }}
                    >
                      {index + 1}
                    </Typography>
                  )}

                  {/* Image */}
                  {!!image && (
                    <Box
                      data-component={'ThreeSteps_step-image'}
                      sx={{
                        position: 'absolute',
                        top: theme.spacing(1.95),
                        right: ShowNumbers ? theme.spacing(2.65) : 'unset',
                        left: !ShowNumbers ? theme.spacing(2.65) : 'unset',
                        borderRadius: '50%',
                        backgroundColor: StepImageBackground
                          ? theme.palette.purple[200]
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Image
                        src={image}
                        alt={step.Image?.alternativeText || cleanedTitle || ''}
                        width={step.Image?.width || ShowNumbers ? 40 : 64}
                        height={step.Image?.height || ShowNumbers ? 40 : 64}
                      />
                    </Box>
                  )}

                  {/* Title */}
                  {step?.Title && (
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      data-component={'ThreeSteps_step-title'}
                      className={titleClasses.length > 0 ? prefixClasses(titleClasses) : undefined}
                      sx={{
                        pl: (!step.Image && ShowNumbers) || (step.Image && !ShowNumbers) ? 12 : 0,
                        // pt: step.Image && ShowNumbers ? 6 : 0,
                        my: 1,
                        width: '100%',
                        lineHeight: 1.2,
                        ml: !!image ? 'md' : 'inherit',
                      }}
                    >
                      {cleanedTitle}
                    </Typography>
                  )}

                  {/* Description */}
                  <Box
                    data-component={'ThreeSteps_step-description'}
                    sx={{
                      flexGrow: 1,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-word',
                      flexWrap: 'wrap',
                      mb: hasCta ? 1 : 0,
                      mt: !step.Title ? 6 : 0,
                      '*:first-child': {
                        mt: 0,
                      },
                      ' p': {
                        mt: 1,
                        '&:last-of-type': {
                          mb: 0,
                        },
                      },
                    }}
                  >
                    {typeof step.Description === 'string' ? (
                      <Typography
                        variant="body2"
                        sx={{
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                      >
                        {step.Description}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                      >
                        <GlobalBlockRenderer content={step.Description} />
                      </Box>
                    )}
                  </Box>

                  {/* CTA Button */}
                  {hasCta && (
                    <Box sx={{ mt: 'auto' }} data-component={'ThreeSteps_step-cta'}>
                      {cta.ButtonLink ? (
                        cta.ButtonLink.startsWith('http') ? (
                          <Button
                            variant="contained"
                            color="primary"
                            href={cta.ButtonLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {cta.ButtonText}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            component={Link}
                            href={cta.ButtonLink}
                          >
                            {cta.ButtonText}
                          </Button>
                        )
                      ) : (
                        <Button variant="contained" color="primary">
                          {cta.ButtonText}
                        </Button>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* Arrow between steps */}
              {!isMobile && !HideArrows && index < validSteps.length - 1 && (
                <ArrowForwardIcon
                  sx={{
                    fontSize: 32,
                    color: theme.palette.purple[700],
                    alignSelf: 'center',
                    mx: 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Stack>

      {BlockCta?.ShowButton && BlockCta?.ButtonText && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}
          data-component={'ThreeSteps_cta'}
        >
          {CTAIntro && (
            <Box sx={{ mx: 'auto' }}>
              <GlobalBlockRenderer content={CTAIntro} />
            </Box>
          )}
          {BlockCta.ButtonLink ? (
            BlockCta.ButtonLink.startsWith('http') ? (
              <Button
                variant="contained"
                color="secondary"
                href={BlockCta.ButtonLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {BlockCta.ButtonText}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                component={Link}
                href={BlockCta.ButtonLink}
              >
                {BlockCta.ButtonText}
              </Button>
            )
          ) : (
            <Button variant="contained" color="secondary">
              {BlockCta.ButtonText}
            </Button>
          )}
          {CTAFooter && (
            <Typography
              variant="tiny"
              align="center"
              sx={{
                color: theme.palette.purple[700],
              }}
            >
              {CTAFooter}
            </Typography>
          )}
        </Box>
      )}
    </CustomBackground>
  );
}
